import React, {useEffect, useMemo, useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {CATEGORY_PROPS} from '../../../globals/Constants';
import {WbIcon} from '@workbench/react';
import {emptyProductSheetObject, IProductSheet,} from '../../../interfaces/IProductSheet';
import {getSheetByNumber} from '../../../utility/FetchAPI';
import {HeaderBanner} from '../headerBanner/HeaderBanner';
import HydrogenHeader from '../../../resource/image/HydrogenHeader.jpg';
import {SheetTable} from './SheetTable';
import {useTranslation} from 'react-i18next';
import '../../../globals/style/GlobalStyle.css';
import './SheetOverview.css';
import {setCategoryString} from '../../../utility/Helper';
import createDOMPurify from 'dompurify';
import {closeSearch} from '../../../utility/DialogHelper';
import moment from 'moment';
import {useLanguageMappings} from '../../../globals/LanguageMappingsContext';
import {Helmet} from 'react-helmet-async';
import ToggleSwitch from '../../general/ToggleSwitch';
import {treeData} from "../../general/marketFilter/treeData";
import {buildTreeIndex} from "../../general/marketFilter/treeIndex";
import {useTreeSelection} from "../../general/marketFilter/useTreeSelection";
import {TreeModal} from "../../general/marketFilter/treeModal";

interface SheetOverviewProps {
    sheetNumber?: string;
    language?: string;
    integrated?: boolean;
}

export const SheetOverview = (props: SheetOverviewProps) => {
    const languageMappings = useLanguageMappings();
    const location = useLocation();
    const {t, i18n} = useTranslation();
    const DOMPurify = createDOMPurify(window);
    // let { sheetNumber, language } = useParams<{
    //   sheetNumber: string;
    //   language: string;
    // }>();
    const params = useParams<{ sheetNumber: string; language: string }>();
    const sheetNumber = props.sheetNumber || params.sheetNumber;
    const language = props.language || params.language || i18n.language;

    const [sheet, setSheet] = useState<IProductSheet>(emptyProductSheetObject);
    const [foundProps, setFoundProps]: any = useState(null);
    const [linkedFluidCount, setLinkedFluidCount] = useState(0);
    const [unlinkedFluidCount, setUnlinkedFluidCount] = useState(0);
    const [showMore, setShowMore] = useState(false);
    const [metaDescription, setMetaDescription] = useState<string | null>(null);
    const [keywords, setKeywords] = useState<string | null>(null);
    const mapping = languageMappings[i18n.language] || languageMappings['en'];
    const basePath = location.pathname.replace(/\/(en|de|pt)$/, '');
    const [isFilterActive, setIsFilterActive] = useState(false);
    const [open, setOpen] = useState(false);
    const index = useMemo(() => buildTreeIndex(treeData), []);
    const {selected, toggleNode, getSelectionState} = useTreeSelection(index);
    const handleToggleChange = (newState: boolean) => {
        setIsFilterActive(newState);
    };
    const regex = /^DTFR \d{2}A.*$/;

    // Create a modified sheet object specifically for the child component
    const filteredSheet = useMemo(() => {
        // If filter is off, pass the original sheet exactly as it is
        if (!isFilterActive) {
            return sheet;
        }

        // Filter the products
        const filteredProducts = sheet.productCollection?.filter(product => {
            let productAreaList = null;
            let hasMatchingArea = null;

            if (product.areaIDs) {
                productAreaList = product.areaIDs.split(";");
                hasMatchingArea = productAreaList.some(id => selected.includes(id));
            }

            const hasMatchingText = product.description?.toLowerCase().includes("daimler truck ag");

            return hasMatchingArea || hasMatchingText;
        }) ?? [];

        // Return a new sheet object with the filtered products injected
        return {
            ...sheet,
            productCollection: filteredProducts
        };
    }, [sheet, isFilterActive, selected]);

    const handleShowMoreToggle = () => {
        setShowMore(!showMore);
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        closeSearch();
        if (sheetNumber != null) {
            getSheetByNumber(sheetNumber).then((results: any) => {
                // console.log('results.data.category:', results.data.category);
                // console.log('CATEGORY_PROPS:', CATEGORY_PROPS);
                const loopProps = CATEGORY_PROPS.find((element) => {
                    const propDesc = setCategoryString(element.description);
                    const resultCat = setCategoryString(results.data.category);
                    setMetaDescription(element.metaDescription[i18n.language as 'en' | 'de' | 'pt']);
                    setKeywords(element.keywords[i18n.language as 'en' | 'de' | 'pt'].join(', '));
                    // console.log(`Comparing: ${propDesc} == ${resultCat}`);
                    return propDesc === resultCat;
                });
                setSheet(results.data);
                setFoundProps(loopProps);

                if (sheetNumber === 'DTFR_16D100') {
                    setFoundProps((prevProps: any) => {
                        return {
                            ...prevProps,
                            image: HydrogenHeader,
                        };
                    });

                }
            });
        }
    }, [sheetNumber, language]);

    return (
        <div>
            {sheet.sheetNumber && sheet.category && (
                <Helmet>
                    <title>
                        {`${t(sheet.sheetNumber, sheet.sheetNumber)} | ${t(foundProps?.description, sheet.category)} | Mercedes-Benz Trucks Operating Fluids`}
                    </title>
                    <meta name="description" content={metaDescription || `Find the best ${t(sheet.category)} oil.`}/>
                    <meta name="keywords" content={keywords || ''}/>
                    <meta name="robots" content="index, follow"/>
                    <link rel="canonical" href={`https://bevo.mercedes-benz-trucks.com${location.pathname}`}/>

                    <html lang={i18n.language}/>
                    <link rel="alternate" href={`https://bevo.mercedes-benz-trucks.com${basePath}/en`} hrefLang="en"/>
                    <link rel="alternate" href={`https://bevo.mercedes-benz-trucks.com${basePath}/de`} hrefLang="de"/>
                    <link rel="alternate" href={`https://bevo.mercedes-benz-trucks.com${basePath}/pt`} hrefLang="pt"/>

                    <meta property="og:title" content={`${metaDescription} | Mercedes-Benz Trucks Operating Fluids`}/>
                    <meta property="og:description"
                          content={metaDescription || `Find the best${t(sheet.category)} oil.`}/>
                    <meta property="og:type" content="website"/>
                    <meta property="og:url" content={`https://bevo.mercedes-benz-trucks.com${location.pathname}`}/>
                    <meta property="og:image" content="https://bevo.mercedes-benz-trucks.com/MB-star_n_web.svg"/>

                    <meta name="twitter:card" content="summary_large_image"/>
                    <meta name="twitter:title"
                          content={`${t(sheet.sheetNumber, sheet.sheetNumber)} | ${t(foundProps?.description, sheet.category)} | Mercedes-Benz Trucks Operating Fluids`}/>
                    <meta name="twitter:description"
                          content={metaDescription || 'Find the best oil for this category.'}/>
                    <meta name="twitter:image" content={foundProps?.image}/>

                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "Product",
                            "name": t(foundProps?.description, sheet.category),
                            "description": metaDescription,
                            "image": foundProps?.image,
                            "url": `https://bevo.mercedes-benz-trucks.com${location.pathname}`
                        })}
                    </script>

                </Helmet>
            )}
            {props.integrated === undefined && (
                <HeaderBanner
                    image={foundProps?.image}
                    description={t(foundProps?.description)}
                />
            )}
            <div style={{marginTop: '120px', marginLeft: '14px', marginRight: '14px'}}>
                <h2 className='wb-type-heading-m textWhite spaceTopLarge spaceBottom'>
                    {sheet.sheetNumber +
                        '   ' + sheet[mapping.title]}
                </h2>
                {sheet.htmlDe === null &&
                    sheet.htmlEn === null &&
                    sheet.productCollection === null && (
                        <p className='text'>
                            {t('NoProduct')}
                        </p>
                    )}
                <p className="text">
                    {sheet[mapping.additionalInfo]}
                </p>
            </div>

            {!regex.test(sheet.sheetNumber) && (
                <>
                    <hr />
                    <ToggleSwitch onToggle={handleToggleChange} active={isFilterActive} label={t('marketFilter')} buttonLabel={t('change')} onChange={() => setOpen(true)}/>
                    <div>
                        <TreeModal
                            open={open}
                            onClose={() => setOpen(false)}
                            tree={treeData}
                            toggleNode={toggleNode}
                            getSelectionState={getSelectionState}
                        />
                        {isFilterActive &&
                            <p style={{
                                color: 'white',
                                padding: '15px',
                                fontFamily: 'MBCorpo Text,DaimlerCS-Regular,sans-serif'
                            }}>{t('mfInfo2')}</p>
                        }
                    </div>
                </>
            )}
            {sheet.publishDate && (
                <p className='textWhite wb-type-label timeLabel'>
                    {t('LastUpdate')}{' '}
                    {
                        moment(
                            sheet.publishDate,
                            [
                                "DD.MM.YY",
                                "DD.MM.YYYY",
                                "DD.MM.YY, HH:mm",
                                "DD.MM.YYYY, HH:mm"
                            ],
                            true
                        ).format(mapping.dateFormat)
                    }
                </p>
            )}
            <SheetTable sheet={filteredSheet} language={`${language}`} showMore={showMore}
                        setLinkedFluidCount={setLinkedFluidCount} setUnlinkedFluidCount={setUnlinkedFluidCount}
                        setShowMore={setShowMore}></SheetTable>
            {sheet?.htmlDe !== null && sheet?.htmlEn !== null && (
                <div className='htmlContainer'>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(sheet[mapping.html || '']),
                        }}
                    />
                    {linkedFluidCount > 0 && unlinkedFluidCount > 0 && (
                        <div className='wb-grid-row rowStyle'>
                            <div className='wb-grid-col-mq1-12 text-center' style={{textAlign: 'center'}}>
                                <button onClick={handleShowMoreToggle}
                                        className='wb-button wb-button--primary wb-button--large'>
                                    {showMore ? t('ShowLess') : t('ShowMore')}
                                    <span style={{marginLeft: '5px'}}>
                                        <WbIcon name={showMore ? 'arrow-up' : 'arrow-down'} className='iconArrow'/>
                                    </span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
